// src/components/Sidebar.js
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Dumbbell,
  BarChart3,
  User,
  LogOut,
  // Imported map/activity only if needed for placeholders, 
  // otherwise using your specific icons:
} from "lucide-react";

export default function CoachSidebar({ selectedProgram }) {
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const isActive = (path) => location.pathname === path;

  // --- 1. DESKTOP SIDEBAR ---
  const DesktopSidebar = () => {
    // Helper to keep code clean
    const navItem = (to, Icon, label) => (
      <Link
        to={to}
        className={`flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-800 transition ${
          isActive(to) ? "bg-gray-800 text-white" : "text-gray-300"
        }`}
        title={collapsed ? label : ""}
      >
        <Icon size={18} />
        {!collapsed && label}
      </Link>
    );

    return (
      <aside
        className={`hidden md:flex flex-col bg-gray-900 text-gray-100 h-screen overflow-hidden transition-all duration-300 border-r border-gray-800 ${
          collapsed ? "w-16" : "w-64"
        }`}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
      >
        <div className="flex flex-col h-screen overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-800 flex flex-col items-center">
            <img
              src={brandLogo}
              alt="Nuvio"
              className="w-8 h-8 rounded-lg object-cover shadow-sm"
            />
            <h1
              className={`text-xl font-bold transition-all duration-300 whitespace-nowrap ${
                collapsed ? "opacity-0 w-0" : "opacity-100"
              }`}
            >
              Nuvio Sport
            </h1>

            {/* Selected Program Box */}
            {!collapsed && selectedProgram && (
              <div className="mt-3 w-full p-2 text-center bg-gray-700 rounded shadow-sm">
                <span className="font-semibold text-sm">{selectedProgram.name}</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-2 overflow-y-auto">
            {navItem("/coach", Home, "Dashboard")}
            {/* {navItem("/coach/team-resources", Dumbbell, "Team Resources")} */}
            {navItem("/coach/combine-scores", BarChart3, "Combine Scores")}
            {navItem("/coach/profile", User, "Profile")}
            
            {/* You can re-enable Home Workouts here if needed in the future */}
            {/* navItem("/skills-workouts", Activity, "Home Workouts") */}

            {/* Logout */}
            <div className="mt-auto pt-4">
              <Link
                to="/logout"
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-800 transition text-gray-300"
              >
                <LogOut size={18} />
                {!collapsed && "Logout"}
              </Link>
            </div>
          </nav>
        </div>
      </aside>
    );
  };

  // --- 2. MOBILE TOP BAR ---
  const MobileTopBar = () => (
    <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-4 flex justify-between items-center shadow-md pt-[env(safe-area-inset-top)] h-[calc(4rem+env(safe-area-inset-top))]">
       <div className="flex flex-col py-2">
          <span className="text-[10px] text-blue-400 font-black uppercase tracking-[0.2em]">Coach View</span>
          <span className="text-white font-bold text-sm truncate max-w-[200px]">
            {selectedProgram?.name || "Nuvio Sport"}
          </span>
       </div>
       
       {/* Profile Shortcut for Mobile Top Bar */}
       <Link 
          to="/coach/profile" 
          className={`p-2 rounded-xl transition-all active:scale-95 ${
            isActive('/coach/profile') 
              ? "bg-blue-600 text-white shadow-lg shadow-blue-900/40" 
              : "bg-gray-800 text-gray-400 border border-white/5"
          }`}
       >
          <User size={18} />
       </Link>
    </div>
  );

  // --- 3. MOBILE BOTTOM NAV ---
  const MobileBottomNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-xl border-t border-gray-800 pt-2 pb-0">
      <div className="grid grid-cols-3 h-16 items-center">
        
        {/* 1. Home */}
        <MobileNavItem 
          to="/coach" 
          icon={Home} 
          label="Home" 
          active={isActive("/coach")} 
        />
  
        {/* 2. Combine */}
        <MobileNavItem 
          to="/coach/combine-scores" 
          icon={BarChart3} 
          label="Combine" 
          active={isActive("/coach/combine-scores")} 
        />
  
        {/* 3. Logout */}
        <MobileNavItem 
          to="/logout" 
          icon={LogOut} 
          label="Logout" 
          active={false} 
        />
        
      </div>
    </div>
  );

  const MobileNavItem = ({ to, icon: Icon, label }) => {
    const active = isActive(to);
    return (
      <Link 
        to={to} 
        className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
          active ? "text-blue-500" : "text-gray-500 hover:text-gray-300"
        }`}
      >
        <Icon size={22} strokeWidth={active ? 2.5 : 2} />
        <span className="text-[9px] font-medium">{label}</span>
      </Link>
    );
  };

  return (
    <>
      <MobileTopBar />
      <DesktopSidebar />
      <MobileBottomNav />
    </>
  );
}
